import { useEffect, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'

interface ConfirmDialogProps {
  open: boolean
  title: string
  description: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Figma `dialog` (2276:306521) — 400px wide, 16px padding, title + body and a
 * right-aligned Cancel / confirm pair.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): ReactElement | null {
  const { t } = useTranslation()

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel()
    }
    globalThis.addEventListener('keydown', onKeyDown)
    return () => {
      globalThis.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onCancel])

  if (!open) return null

  return (
    <>
      <div
        data-name="bg-overlay"
        className="bg-overlay fixed inset-0 z-60 backdrop-blur-[2px]"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div
        data-node-id="2276:306521"
        data-name="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="hub-dialog fixed top-1/2 left-1/2 z-60 flex w-[calc(100%-48px)] max-w-100 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-4 rounded-md p-4"
      >
        <div className="flex w-full flex-col items-start gap-2 px-1 pt-1">
          <h2 className="hub-headline-lg text-dialog-text">{title}</h2>
          <p className="hub-body-sm text-dialog-text w-full">{description}</p>
        </div>

        <div className="flex w-full items-center justify-end gap-3">
          <Button variant="secondary" size="md" onClick={onCancel}>
            {t('actions.cancel')}
          </Button>
          <Button variant="primary" size="md" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </>
  )
}
